import { Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormID } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  ActionButtonOutcomesType,
  ActionButtonWidgetActionsOutcomesNavigationConfigMap,
  BaseWidgetProperties,
  ButtonActionType,
  UserInfoType,
  UserMarketingPreference,
  UserProfileData,
  actionButtonNavigationConfigType,
  formatNameAsUrl,
} from '@finxone-platform/shared/sys-config-types';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { take, tap } from 'rxjs';
import { ClearFormDataActionWithId, ResetFormDataAction } from '../../../actions/form-submission.action';
import {
  SetProfile,
  UserProfileUpdateFailed,
  UserProfileUpdated,
} from '../../../actions/user-profile.action';
import { FormActionState } from '../../../state/form-submision.state';
import { ProfileState } from '../../../state/user-profile.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { redirectToPage } from '../cta-button-actions.utils';
import { transformUserFormDataIntoRequestPayload } from '../onboarding-zone/onboarding-actions.utils';

/**
 * Handles submission of the user verification form.
 *
 * - Extracts verification data from form state.
 * - Dispatches a profile update action.
 * - Waits for success or failure responses.
 * - Navigates to the configured success/failure page.
 * - Resets form data after navigation.
 *
 */

export const submitUserProfileUpdate = (widgetProperties: BaseWidgetProperties, injector: Injector): void => {
  const router = injector.get(Router);
  const store = injector.get(Store);
  const actions = injector.get(Actions);
  const alertHandlerService = injector.get(AlertHandlerService);
  const activeRoute = injector.get(ActivatedRoute);

  const navigationConfig = widgetProperties?.actionNavigationConfigurations;
  const formState = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormID.UPDATE_USER_PROFILE),
  );
  const formValue = formState?.formData;

  if (!formValue) return;

  const zoneUrlName = activeRoute.snapshot.paramMap.get('zone');
  const isVerificationZone = zoneUrlName === formatNameAsUrl(APP_ZONES.VERIFICATION);
  const excludeKeys = ['marketingPreferences'];
  const baseRequestData = transformUserFormDataIntoRequestPayload(
    formValue,
    undefined,
    undefined,
    false,
    excludeKeys,
  );

  // marketingPreferences is handled separately because the multi-select form field returns an array
  // of selected keys (e.g. ["email", "sms"]), but the API expects a boolean map over all keys:
  // { email: true, sms: true, push: false }
  if (formValue?.marketingPreferences) {
    const marketingPreferences = store.selectSnapshot(ProfileState.getMarketingPreferences);
    const payload: UserMarketingPreference = {} as UserMarketingPreference;
    Object.keys(marketingPreferences).forEach((key) => {
      payload[key as keyof UserMarketingPreference] = !!formValue.marketingPreferences.includes(key);
    });
    (baseRequestData as UserProfileData).marketingPreferences = payload;
  }

  const requestData = {
    ...baseRequestData,
    ...(isVerificationZone && { profileCompletion: true }),
  } as UserInfoType;

  store.dispatch(new SetProfile(requestData, false));

  /**
   * Handles navigation based on action outcome.
   */
  const defaultNavigation = ActionButtonWidgetActionsOutcomesNavigationConfigMap.get(
    ButtonActionType.SUBMIT_USER_PROFILE_UPDATE,
  );
  const handleNavigation = (outcome: ActionButtonOutcomesType): void => {
    const config =
      navigationConfig?.[outcome] ?? (defaultNavigation as actionButtonNavigationConfigType)[outcome];
    const navigationProps: BaseWidgetProperties = {
      textContent: '',
      zoneToNavigate: config?.selectedZone,
      urlToNavigate: config?.selectedPageUrl,
    };

    redirectToPage(router, navigationProps, injector);
    store.dispatch(new ResetFormDataAction());
  };

  // Listen for successful profile update
  actions
    .pipe(
      ofActionDispatched(UserProfileUpdated),
      take(1),
      tap(() => {
        if (widgetProperties?.['successAlertType']) {
          findAndShowAlertFromCode(store, router, alertHandlerService, [
            widgetProperties?.['successAlertType'],
          ]);
        }
        store.dispatch(new ClearFormDataActionWithId(FormID.UPDATE_USER_PROFILE));
        handleNavigation(ActionButtonOutcomesType.SUCCESS);
      }),
    )
    .subscribe();

  // Listen for failed profile update
  actions
    .pipe(
      ofActionDispatched(UserProfileUpdateFailed),
      take(1),
      tap(() => {
        if (widgetProperties?.['errorAlertType']) {
          findAndShowAlertFromCode(store, router, alertHandlerService, [
            widgetProperties?.['errorAlertType'],
          ]);
        }
        store.dispatch(new ClearFormDataActionWithId(FormID.UPDATE_USER_PROFILE));
        handleNavigation(ActionButtonOutcomesType.FAILURE);
      }),
    )
    .subscribe();
};
